// Photographies d'inscriptions hiéroglyphiques (temples, stèles, objets) sous
// licence libre, servies depuis public/images/kama/temples/.
//
// Chaque image garde son auteur, sa licence et sa page source : les licences
// CC BY / CC BY-SA OBLIGENT à afficher ce crédit là où l'image est montrée.
// Les « signes repérés » renvoient uniquement à des fiches qui existent déjà
// dans le corpus Kama (catégorie + clé), pour éviter tout glyphe Unicode
// non vérifié.

export type TempleImageLicense =
  | 'CC0'
  | 'Domaine public'
  | 'CC BY 2.0'
  | 'CC BY 3.0'
  | 'CC BY 4.0'
  | 'CC BY-SA 3.0';

export interface TempleImageLink {
  category: string; // nom de catégorie tel que dans categories/index.ts
  key: string;      // clé de la fiche dans cette catégorie
}

export interface TempleImageSign {
  /** Codes Gardiner visibles sur la photo, ex. 'L2' ou 'D21-X8-X1'. */
  gardiner?: string;
  note: string;
  noteEn: string;
  /** true quand la lecture est une proposition, pas une certitude. */
  tentative?: boolean;
  link?: TempleImageLink;
}

export interface TempleImage {
  id: string;
  src: string;
  title: string;
  titleEn: string;
  site: string;
  period: string;
  periodEn: string;
  description: string;
  descriptionEn: string;
  signs: TempleImageSign[];
  credit: {
    author: string;
    license: TempleImageLicense;
    licenseUrl?: string;
    sourceUrl: string;
  };
}

const BASE = '/images/kama/temples/';

export const templeImages: TempleImage[] = [
  {
    id: 'medinet-habu-tuile',
    src: `${BASE}medinet-habu-tuile.webp`,
    title: 'Tuile du palais de Ramsès III',
    titleEn: 'Tile from the palace of Ramesses III',
    site: 'Medinet Habu (Thèbes ouest)',
    period: 'Nouvel Empire, XXᵉ dynastie, v. 1182-1151 av. J.-C.',
    periodEn: 'New Kingdom, 20th Dynasty, c. 1182-1151 BCE',
    description:
      'Tuile en faïence et verre qui décorait une porte du palais. Les signes forment un vœu : « que tout le peuple adore le roi Ramsès III ». Le vanneau aux mains levées (le peuple), l\'étoile (adorer) et la corbeille (tout) se lisent comme un rébus, face au cartouche royal.',
    descriptionEn:
      'Faience and glass tile that decorated a palace doorway. The signs form a wish: "may all the people praise King Ramesses III". The lapwing with raised hands (the people), the star (to adore) and the basket (all) read as a rebus, facing the royal cartouche.',
    signs: [
      {
        gardiner: 'G23',
        note: 'Le vanneau huppé aux mains levées : rḫyt, « le peuple ».',
        noteEn: 'The crested lapwing with raised hands: rḫyt, "the people".',
        link: { category: 'Objets sacrés et du quotidien', key: 'rḫyt' },
      },
      {
        gardiner: 'N14',
        note: 'L\'étoile sous le vanneau : dwꜣ, « adorer ».',
        noteEn: 'The star below the lapwing: dwꜣ, "to adore".',
      },
      {
        gardiner: 'V30',
        note: 'La corbeille sur laquelle repose l\'oiseau : nb, « tout, chaque ».',
        noteEn: 'The basket the bird rests on: nb, "all, every".',
      },
      {
        note: 'Le cartouche de Ramsès III, avec le disque solaire de Rê en tête.',
        noteEn: 'The cartouche of Ramesses III, with Ra\'s sun disk at the top.',
        link: { category: 'Pharaons', key: 'Rꜥ-ms-sw-III' },
      },
    ],
    credit: {
      author: 'Daderot (Oriental Institute Museum, Université de Chicago)',
      license: 'CC0',
      licenseUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
      sourceUrl:
        'https://commons.wikimedia.org/wiki/File:Tile_with_hieroglyphic_inscription,_Luxor,_Medinet_Habu,_New_Kingdom,_Dynasty_20,_reign_of_Ramesses_III,_c._1182-1151_BC,_faience,_glaze,_glass_-_Oriental_Institute_Museum,_University_of_Chicago_-_DSC07921.JPG',
    },
  },
  {
    id: 'karnak-thoutmosis2',
    src: `${BASE}karnak-thoutmosis2.webp`,
    title: 'Le roi offre le vin',
    titleEn: 'The king offers wine',
    site: 'Karnak (Thèbes)',
    period: 'Nouvel Empire, XVIIIᵉ dynastie (Thoutmosis II)',
    periodEn: 'New Kingdom, 18th Dynasty (Thutmose II)',
    description:
      'Le roi, agenouillé, présente deux vases au dieu. En retour, une main divine lui tend le signe de vie et le sceptre ouas. La colonne de droite est la légende de la scène.',
    descriptionEn:
      'The kneeling king presents two jars to the god. In return, a divine hand holds out the sign of life and the was-sceptre to him. The right-hand column is the caption of the scene.',
    signs: [
      {
        gardiner: 'S34',
        note: 'La croix ankh tendue au roi : ꜥnḫ, « vie ».',
        noteEn: 'The ankh held out to the king: ꜥnḫ, "life".',
        link: { category: 'Objets sacrés et du quotidien', key: 'ꜥnḫ' },
      },
      {
        gardiner: 'S40',
        note: 'Le sceptre ouas, tenu avec l\'ankh : wꜣs, « puissance ».',
        noteEn: 'The was-sceptre, held together with the ankh: wꜣs, "power".',
        link: { category: 'Objets sacrés et du quotidien', key: 'wꜣs' },
      },
      {
        gardiner: 'D21-X8-X1 … M17-D21-Q3-W21',
        note: 'Légende de droite : rdỉt jrp, « donner le vin » ; les deux jarres (W21) servent de déterminatif.',
        noteEn: 'Right-hand caption: rdỉt jrp, "giving wine"; the two jars (W21) act as determinative.',
        tentative: true,
        link: { category: 'Nourriture', key: 'jrp' },
      },
    ],
    credit: {
      author: 'Wmpearl ; travail dérivé : JMCC1',
      license: 'CC0',
      licenseUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
      sourceUrl:
        'https://commons.wikimedia.org/wiki/File:Stone_block_with_relief_at_Karnak_Temple_Thutmosis_II.jpg',
    },
  },
  {
    id: 'abydos-relief',
    src: `${BASE}abydos-relief.webp`,
    title: 'Les « hiéroglyphes hélicoptère » d\'Abydos',
    titleEn: 'The "helicopter hieroglyphs" of Abydos',
    site: 'Abydos, temple de Séthi Iᵉʳ',
    period: 'Nouvel Empire, XIXᵉ dynastie',
    periodEn: 'New Kingdom, 19th Dynasty',
    description:
      'Célèbre linteau où l\'on croit voir un hélicoptère. C\'est en fait un palimpseste : les titres de Séthi Iᵉʳ ont été rebouchés au plâtre puis regravés au nom de Ramsès II. Le plâtre tombé, les deux textes se superposent.',
    descriptionEn:
      'Famous lintel where people think they see a helicopter. It is actually a palimpsest: the titles of Seti I were filled in with plaster and recarved for Ramesses II. Once the plaster fell, the two texts overlap.',
    signs: [
      {
        gardiner: 'M23-L2',
        note: 'À gauche, le jonc et l\'abeille : nsw-bỉty, « roi de Haute et de Basse-Égypte ».',
        noteEn: 'On the left, the sedge and the bee: nsw-bỉty, "King of Upper and Lower Egypt".',
        link: { category: 'Objets sacrés et du quotidien', key: 'bity' },
      },
      {
        note: 'Ville d\'Abydos.',
        noteEn: 'The city of Abydos.',
        link: { category: 'Villes', key: 'Ꜣbḏw' },
      },
    ],
    credit: {
      author: 'Olek95',
      license: 'Domaine public',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Hieroglif_z_Abydos.jpg',
    },
  },
  {
    id: 'edfou-temple',
    src: `${BASE}edfou-temple.webp`,
    title: 'Le roi couronné par Nekhbet et Ouadjet',
    titleEn: 'The king crowned by Nekhbet and Wadjet',
    site: 'Edfou, temple d\'Horus',
    period: 'Époque ptolémaïque (Ptolémée VIII)',
    periodEn: 'Ptolemaic period (Ptolemy VIII)',
    description:
      'Ptolémée VIII, coiffé de la double couronne, entre les deux déesses protectrices du pays : à gauche Nekhbet avec la couronne blanche (Haute-Égypte), à droite Ouadjet avec la couronne rouge (Basse-Égypte). Les deux cartouches du roi sont au-dessus de sa tête.',
    descriptionEn:
      'Ptolemy VIII, wearing the double crown, between the two protective goddesses of the land: Nekhbet with the white crown (Upper Egypt) on the left, Wadjet with the red crown (Lower Egypt) on the right. The king\'s two cartouches are above his head.',
    signs: [
      {
        note: 'Couronne blanche de Nekhbet.',
        noteEn: 'Nekhbet\'s white crown.',
        link: { category: 'Objets sacrés et du quotidien', key: 'ḥḏ.t' },
      },
      {
        note: 'Couronne rouge d\'Ouadjet.',
        noteEn: 'Wadjet\'s red crown.',
        link: { category: 'Objets sacrés et du quotidien', key: 'dšr.t' },
      },
      {
        note: 'Le roi porte le pschent, union des deux couronnes.',
        noteEn: 'The king wears the pschent, the union of both crowns.',
        link: { category: 'Objets sacrés et du quotidien', key: 'šmty' },
      },
      {
        note: 'Nekhbet, déesse-vautour de Haute-Égypte.',
        noteEn: 'Nekhbet, vulture goddess of Upper Egypt.',
        link: { category: 'Divinités', key: 'Nḫbt' },
      },
      {
        note: 'Ouadjet, déesse-cobra de Basse-Égypte.',
        noteEn: 'Wadjet, cobra goddess of Lower Egypt.',
        link: { category: 'Divinités', key: 'Wꜣḏyt' },
      },
    ],
    credit: {
      author: 'Olaf Tausch',
      license: 'CC BY 3.0',
      licenseUrl: 'https://creativecommons.org/licenses/by/3.0/',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Edfu_Tempel_42.jpg',
    },
  },
  {
    id: 'philae-temple',
    src: `${BASE}philae-temple.webp`,
    title: 'Offrande à Isis et à l\'enfant Horus',
    titleEn: 'Offering to Isis and the child Horus',
    site: 'Philae, temple d\'Isis',
    period: 'Époque ptolémaïque (Ptolémée II)',
    periodEn: 'Ptolemaic period (Ptolemy II)',
    description:
      'Ptolémée II présente deux vases à Isis, assise sur son trône et allaitant son fils Horus enfant (Harpocrate). Derrière elle se tient la déesse Anouket, à la couronne de plumes. Isis porte les cornes de vache et le disque solaire.',
    descriptionEn:
      'Ptolemy II presents two jars to Isis, seated on her throne and nursing her son, the child Horus (Harpocrates). Behind her stands the goddess Anuket, with her feathered crown. Isis wears cow horns and the sun disk.',
    signs: [
      {
        note: 'Isis, la mère divine.',
        noteEn: 'Isis, the divine mother.',
        link: { category: 'Divinités', key: 'Ỉs.t' },
      },
      {
        note: 'Horus enfant sur les genoux d\'Isis.',
        noteEn: 'The child Horus on Isis\'s lap.',
        link: { category: 'Divinités', key: 'Ḥr-pꜣ-ẖrd' },
      },
      {
        gardiner: 'S34',
        note: 'Plusieurs ankh dans les colonnes de texte : ꜥnḫ, « vie ».',
        noteEn: 'Several ankhs in the text columns: ꜥnḫ, "life".',
        link: { category: 'Objets sacrés et du quotidien', key: 'ꜥnḫ' },
      },
      {
        note: 'L\'île de Philae.',
        noteEn: 'The island of Philae.',
        link: { category: 'Villes', key: 'Pꜣ jꜣrk' },
      },
    ],
    credit: {
      author: 'Olaf Tausch',
      license: 'CC BY 3.0',
      licenseUrl: 'https://creativecommons.org/licenses/by/3.0/',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Philae_12.jpg',
    },
  },
  {
    id: 'kom-ombo',
    src: `${BASE}kom-ombo.webp`,
    title: 'Procession de déesses lionnes',
    titleEn: 'Procession of lioness goddesses',
    site: 'Kom Ombo, temple de Sobek et Haroëris',
    period: 'Époque ptolémaïque',
    periodEn: 'Ptolemaic period',
    description:
      'Déesses à tête de lionne, coiffées du disque solaire, tenant un sceptre-papyrus et la croix de vie. Au-dessus, les colonnes de texte donnent leurs noms.',
    descriptionEn:
      'Lion-headed goddesses crowned with the sun disk, holding a papyrus sceptre and the sign of life. Above them, the text columns give their names.',
    signs: [
      {
        gardiner: 'S34',
        note: 'Chaque déesse tient l\'ankh : ꜥnḫ, « vie ».',
        noteEn: 'Each goddess holds the ankh: ꜥnḫ, "life".',
        link: { category: 'Objets sacrés et du quotidien', key: 'ꜥnḫ' },
      },
      {
        note: 'La plus connue des déesses lionnes est Sekhmet (les noms exacts de ces déesses ne sont pas lus ici).',
        noteEn: 'The best known of the lioness goddesses is Sekhmet (these goddesses\' exact names are not read here).',
        tentative: true,
        link: { category: 'Divinités', key: 'Sḫm.t' },
      },
    ],
    credit: {
      author: 'Ijanderson977 ; travail dérivé : JMCC1',
      license: 'Domaine public',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Kom_Ombo_05_977.jpg',
    },
  },
  {
    id: 'stele-dedou',
    src: `${BASE}stele-dedou.webp`,
    title: 'Stèle de Dedou et Sitsobek',
    titleEn: 'Stela of Dedu and Sitsobek',
    site: 'Metropolitan Museum of Art, New York (16.10.333)',
    period: 'Moyen Empire, fin XIᵉ – début XIIᵉ dynastie, v. 2000-1952 av. J.-C.',
    periodEn: 'Middle Kingdom, late 11th – early 12th Dynasty, c. 2000-1952 BCE',
    description:
      'Stèle peinte où les couleurs d\'origine sont bien conservées. Dedou et son épouse Sitsobek se tiennent devant une table d\'offrandes. Les deux lignes du haut, peintes en bleu, se lisent de droite à gauche, dans le sens où regardent les signes.',
    descriptionEn:
      'Painted stela with well-preserved original colours. Dedu and his wife Sitsobek stand before an offering table. The two top lines, painted blue, read from right to left, in the direction the signs face.',
    signs: [
      {
        note: 'Bon exemple pour apprendre le sens de lecture : on lit vers le visage des personnages et des animaux.',
        noteEn: 'Good example for learning reading direction: you read towards the faces of people and animals.',
      },
    ],
    credit: {
      author: 'Metropolitan Museum of Art (Open Access)',
      license: 'CC0',
      licenseUrl: 'https://creativecommons.org/publicdomain/zero/1.0/',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Stela_of_Dedu_MET_16.10.333.jpg',
    },
  },
  {
    id: 'edfou-cartouche',
    src: `${BASE}edfou-cartouche.webp`,
    title: 'Un cartouche royal à Edfou',
    titleEn: 'A royal cartouche at Edfu',
    site: 'Edfou, salle du trésor du temple d\'Horus',
    period: 'Époque ptolémaïque',
    periodEn: 'Ptolemaic period',
    description:
      'Gros plan sur un cartouche : l\'anneau allongé qui entoure le nom du roi, fermé par un nœud à droite.',
    descriptionEn:
      'Close-up of a cartouche: the elongated ring surrounding the king\'s name, closed by a knot on the right.',
    signs: [
      {
        gardiner: 'V10',
        note: 'Le cartouche lui-même, šnw.',
        noteEn: 'The cartouche itself, šnw.',
        link: { category: 'Objets sacrés et du quotidien', key: 'šnw-cartouche' },
      },
    ],
    credit: {
      author: 'Rémih (photo d\'origine) ; retouche : -sche',
      license: 'CC BY-SA 3.0',
      licenseUrl: 'https://creativecommons.org/licenses/by-sa/3.0/',
      sourceUrl:
        'https://commons.wikimedia.org/wiki/File:Edfu_15_cartouche_hieroglyphs_decolorized.jpg',
    },
  },
  {
    id: 'medinet-habu-temple',
    src: `${BASE}medinet-habu-temple.webp`,
    title: 'La victoire sur les Peuples de la Mer',
    titleEn: 'The victory over the Sea Peoples',
    site: 'Medinet Habu, deuxième pylône',
    period: 'Nouvel Empire, an 8 de Ramsès III',
    periodEn: 'New Kingdom, year 8 of Ramesses III',
    description:
      'Un mur entier couvert de texte : le récit officiel de la victoire de Ramsès III sur les Peuples de la Mer. Dans les lignes du bas, on repère facilement des ankh et des cartouches.',
    descriptionEn:
      'An entire wall covered in text: the official account of Ramesses III\'s victory over the Sea Peoples. In the bottom lines, ankhs and cartouches are easy to spot.',
    signs: [
      {
        note: 'Ramsès III, auteur de cette inscription.',
        noteEn: 'Ramesses III, author of this inscription.',
        link: { category: 'Pharaons', key: 'Rꜥ-ms-sw-III' },
      },
      {
        note: 'Medinet Habu se trouve à Thèbes, sur la rive ouest.',
        noteEn: 'Medinet Habu lies in Thebes, on the west bank.',
        link: { category: 'Villes', key: 'Wꜣst' },
      },
    ],
    credit: {
      author: 'Olaf Tausch',
      license: 'CC BY 3.0',
      licenseUrl: 'https://creativecommons.org/licenses/by/3.0/',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:Medinet_Habu_Ramses_III._Tempel_14.JPG',
    },
  },
  {
    id: 'abydos-seti1',
    src: `${BASE}abydos-seti1.webp`,
    title: 'Le temple de Séthi Iᵉʳ à Abydos',
    titleEn: 'The temple of Seti I at Abydos',
    site: 'Abydos',
    period: 'Nouvel Empire, XIXᵉ dynastie',
    periodEn: 'New Kingdom, 19th Dynasty',
    description:
      'Un pilier sculpté d\'une figure royale et, à droite, un mur couvert de colonnes de hiéroglyphes. Abydos était le grand centre du culte d\'Osiris.',
    descriptionEn:
      'A pillar carved with a royal figure and, on the right, a wall covered in columns of hieroglyphs. Abydos was the great centre of the cult of Osiris.',
    signs: [
      {
        note: 'Ville d\'Abydos.',
        noteEn: 'The city of Abydos.',
        link: { category: 'Villes', key: 'Ꜣbḏw' },
      },
    ],
    credit: {
      author: 'Vyacheslav Argenberg',
      license: 'CC BY 4.0',
      licenseUrl: 'https://creativecommons.org/licenses/by/4.0/',
      sourceUrl:
        'https://commons.wikimedia.org/wiki/File:Temple_of_Seti_I,_Egyptian_hieroglyphs,_Abydos,_Egypt.jpg',
    },
  },
  {
    id: 'louxor-temple',
    src: `${BASE}louxor-temple.webp`,
    title: 'Relief du temple de Louxor',
    titleEn: 'Relief at Luxor Temple',
    site: 'Louxor (Thèbes)',
    period: 'Nouvel Empire',
    periodEn: 'New Kingdom',
    description:
      'Relief en lumière rasante : un personnage royal et, à gauche, des colonnes de hiéroglyphes, dont des cartouches.',
    descriptionEn:
      'Relief in raking light: a royal figure and, on the left, columns of hieroglyphs, including cartouches.',
    signs: [
      {
        note: 'Louxor fait partie de l\'ancienne Thèbes.',
        noteEn: 'Luxor is part of ancient Thebes.',
        link: { category: 'Villes', key: 'Wꜣst' },
      },
    ],
    credit: {
      author: 'Jorge Láscar',
      license: 'CC BY 2.0',
      licenseUrl: 'https://creativecommons.org/licenses/by/2.0/',
      sourceUrl:
        'https://commons.wikimedia.org/wiki/File:Egyptian_hieroglyphs_-_Luxor_Temple_(14261740785).jpg',
    },
  },
  {
    id: 'pierre-de-rosette',
    src: `${BASE}pierre-de-rosette.webp`,
    title: 'La pierre de Rosette',
    titleEn: 'The Rosetta Stone',
    site: 'British Museum, Londres',
    period: 'Époque ptolémaïque, 196 av. J.-C. (Ptolémée V)',
    periodEn: 'Ptolemaic period, 196 BCE (Ptolemy V)',
    description:
      'Le même décret écrit trois fois : en hiéroglyphes (en haut), en démotique (au milieu) et en grec (en bas). C\'est en comparant le cartouche de Ptolémée avec le grec que Champollion a commencé le déchiffrement en 1822.',
    descriptionEn:
      'The same decree written three times: in hieroglyphs (top), in Demotic (middle) and in Greek (bottom). It was by comparing Ptolemy\'s cartouche with the Greek that Champollion began the decipherment in 1822.',
    signs: [
      {
        note: 'Le nom de Ptolémée, épelé signe par signe dans un cartouche.',
        noteEn: 'Ptolemy\'s name, spelled sign by sign inside a cartouche.',
        link: { category: 'Lire les hiéroglyphes', key: 'Ptwlmys' },
      },
    ],
    credit: {
      author: 'E. A. Wallis Budge, The Rosetta Stone (1922) ; photo Donald Macbeth',
      license: 'Domaine public',
      sourceUrl: 'https://commons.wikimedia.org/wiki/File:The_Rosetta_Stone.jpg',
    },
  },
];

export default templeImages;
